import {
  validateAppointmentWaitlistDraft,
  type AppointmentWaitlistDraftV3,
} from "./appointmentWaitlistDraft";
import {
  haveExactAppointmentWaitlistConfigurationAuthority,
  validateAppointmentWaitlistConfigurationPublication,
  validateAppointmentWaitlistPreferencesForConfiguration,
  type AppointmentWaitlistConfigurationPublicationV1,
  type AppointmentWaitlistPreferences,
} from "./appointmentWaitlistConfiguration";
import { isCustomerOwnedSessionStorageQuarantined } from "@/app/booking/core/customerOwnedSessionStorageBarrier";
import { migrateRetiredGroupRowsFromSharedAppointmentWaitlistStorage } from "./retiredGroupAppointmentWaitlistStorageMigration";
import { APPOINTMENT_WAITLIST_PENDING_TRANSITION_STORAGE_MAX_SERIALIZED_CHARACTERS } from "./appointmentWaitlistStorageLimits";

export const APPOINTMENT_WAITLIST_PENDING_TRANSITION_STORAGE_KEY =
  "volera:appointment-waitlist-pending-transition:v1";
const MAX_AGE_MS = 30 * 60_000;
const MAX_PENDING_TRANSITIONS = 12;

function safePendingTransitionEpoch(value?: number) {
  try {
    const epoch = value ?? Date.now();
    return Number.isSafeInteger(epoch) &&
      epoch >= 0 &&
      epoch <= 8_640_000_000_000_000
      ? epoch
      : null;
  } catch {
    return null;
  }
}

export interface AppointmentWaitlistPendingTransitionV1 {
  schemaVersion: 1;
  kind: "continue-after-auth";
  waitlistDraftId: string;
  sourceAppointmentDraftId: string;
  expectedCustomerAccountId: string | null;
  preferences: AppointmentWaitlistPreferences;
  configurationPublication: AppointmentWaitlistConfigurationPublicationV1;
  createdAtEpochMs: number;
}

function isIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= 160
  );
}

interface AppointmentWaitlistPendingTransitionStoreV1 {
  schemaVersion: 1;
  entries: Record<string, AppointmentWaitlistPendingTransitionV1>;
}

function readRaw(evaluatedAtEpochMs?: number) {
  if (typeof window === "undefined") return null;
  try {
    if (
      !migrateRetiredGroupRowsFromSharedAppointmentWaitlistStorage(
        evaluatedAtEpochMs,
      )
    ) {
      return null;
    }
    const raw = window.sessionStorage.getItem(
      APPOINTMENT_WAITLIST_PENDING_TRANSITION_STORAGE_KEY,
    );
    if (
      raw !== null &&
      raw.length >
        APPOINTMENT_WAITLIST_PENDING_TRANSITION_STORAGE_MAX_SERIALIZED_CHARACTERS
    ) {
      return null;
    }
    return JSON.parse(raw ?? "null") as unknown;
  } catch {
    return null;
  }
}

function readEntries(now: number) {
  const value = readRaw(now);
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const raw = value as Record<string, unknown>;
  if (
    raw.schemaVersion !== 1 ||
    !raw.entries ||
    typeof raw.entries !== "object" ||
    Array.isArray(raw.entries)
  )
    return {};
  return Object.fromEntries(
    Object.entries(raw.entries as Record<string, unknown>)
      .map(([draftId, pending]) => [draftId, validatePending(pending, now)] as const)
      .filter(
        (entry): entry is readonly [string, AppointmentWaitlistPendingTransitionV1] =>
          Boolean(entry[1] && entry[1].waitlistDraftId === entry[0]),
      ),
  );
}

function writeEntries(
  entries: Record<string, AppointmentWaitlistPendingTransitionV1>,
  evaluatedAtEpochMs?: number,
) {
  if (typeof window === "undefined") return false;
  const value: AppointmentWaitlistPendingTransitionStoreV1 = {
    schemaVersion: 1,
    entries,
  };
  try {
    if (
      !migrateRetiredGroupRowsFromSharedAppointmentWaitlistStorage(
        evaluatedAtEpochMs,
      )
    ) {
      return false;
    }
    const serialized = JSON.stringify(value);
    if (
      serialized.length >
      APPOINTMENT_WAITLIST_PENDING_TRANSITION_STORAGE_MAX_SERIALIZED_CHARACTERS
    ) {
      return false;
    }
    window.sessionStorage.setItem(
      APPOINTMENT_WAITLIST_PENDING_TRANSITION_STORAGE_KEY,
      serialized,
    );
    return true;
  } catch {
    return false;
  }
}

function validatePending(
  value: unknown,
  now: number,
): AppointmentWaitlistPendingTransitionV1 | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const configuration = validateAppointmentWaitlistConfigurationPublication(
    raw.configurationPublication,
  );
  if (
    raw.schemaVersion !== 1 ||
    raw.kind !== "continue-after-auth" ||
    !isIdentifier(raw.waitlistDraftId) ||
    !isIdentifier(raw.sourceAppointmentDraftId) ||
    (raw.expectedCustomerAccountId !== null &&
      !isIdentifier(raw.expectedCustomerAccountId)) ||
    !Number.isSafeInteger(raw.createdAtEpochMs) ||
    (raw.createdAtEpochMs as number) > now + 5 * 60_000 ||
    (raw.createdAtEpochMs as number) + MAX_AGE_MS <= now ||
    !configuration ||
    configuration.waitlistDraftId !== raw.waitlistDraftId ||
    configuration.sourceAppointmentDraftId !== raw.sourceAppointmentDraftId
  ) {
    return null;
  }
  const preferences = validateAppointmentWaitlistPreferencesForConfiguration({
    publication: configuration,
    preferences: raw.preferences as AppointmentWaitlistPreferences,
  });
  if (!preferences) return null;
  return {
    schemaVersion: 1,
    kind: "continue-after-auth",
    waitlistDraftId: raw.waitlistDraftId,
    sourceAppointmentDraftId: raw.sourceAppointmentDraftId,
    expectedCustomerAccountId: raw.expectedCustomerAccountId,
    preferences,
    configurationPublication: configuration,
    createdAtEpochMs: raw.createdAtEpochMs as number,
  };
}

export function storeAppointmentWaitlistPendingTransition(
  draftValue: AppointmentWaitlistDraftV3,
  options: { expectedCustomerAccountId?: string | null; now?: number } = {},
) {
  const now = safePendingTransitionEpoch(options.now);
  if (now === null) return false;
  const draft = validateAppointmentWaitlistDraft(draftValue);
  if (!draft.valid || draft.draft.stage !== "preferences") return false;
  const pending: AppointmentWaitlistPendingTransitionV1 = {
    schemaVersion: 1,
    kind: "continue-after-auth",
    waitlistDraftId: draft.draft.draftId,
    sourceAppointmentDraftId: draft.draft.appointmentDraft.draftId,
    expectedCustomerAccountId:
      options.expectedCustomerAccountId ?? draft.draft.customerAccountId,
    preferences: draft.draft.preferences,
    configurationPublication: draft.draft.configurationPublication,
    createdAtEpochMs: now,
  };
  if (typeof window === "undefined") return false;
  const entries = readEntries(now);
  entries[pending.waitlistDraftId] = pending;
  const bounded = Object.fromEntries(
    Object.entries(entries)
      .sort(([, left], [, right]) =>
        right.createdAtEpochMs - left.createdAtEpochMs,
      )
      .slice(0, MAX_PENDING_TRANSITIONS),
  );
  return (
    writeEntries(bounded, now) &&
    Boolean(readEntries(now)[pending.waitlistDraftId])
  );
}

export function readAppointmentWaitlistPendingTransition(
  draftValue: AppointmentWaitlistDraftV3,
  options: { currentCustomerAccountId?: string | null; now?: number } = {},
) {
  const now = safePendingTransitionEpoch(options.now);
  if (now === null) return null;
  const draft = validateAppointmentWaitlistDraft(draftValue);
  const pending = draft.valid ? readEntries(now)[draft.draft.draftId] : null;
  if (
    !draft.valid ||
    draft.draft.stage !== "preferences" ||
    !pending ||
    pending.waitlistDraftId !== draft.draft.draftId ||
    pending.sourceAppointmentDraftId !== draft.draft.appointmentDraft.draftId ||
    (pending.expectedCustomerAccountId !== null &&
      pending.expectedCustomerAccountId !==
        options.currentCustomerAccountId) ||
    JSON.stringify(pending.preferences) !== JSON.stringify(draft.draft.preferences) ||
    !haveExactAppointmentWaitlistConfigurationAuthority(
      pending.configurationPublication,
      draft.draft.configurationPublication,
    )
  ) {
    return null;
  }
  return pending;
}

export function inspectAppointmentWaitlistPendingTransitionsPersisted(
  evaluatedAtEpochMs?: number,
) {
  const now = safePendingTransitionEpoch(evaluatedAtEpochMs);
  if (now === null) {
    return { status: "unreadable", transitions: [] as const } as const;
  }
  if (
    typeof window === "undefined" ||
    isCustomerOwnedSessionStorageQuarantined()
  ) {
    return { status: "unreadable", transitions: [] as const } as const;
  }
  try {
    if (!migrateRetiredGroupRowsFromSharedAppointmentWaitlistStorage(now)) {
      return { status: "unreadable", transitions: [] as const } as const;
    }
    const raw = window.sessionStorage.getItem(
      APPOINTMENT_WAITLIST_PENDING_TRANSITION_STORAGE_KEY,
    );
    if (raw === null) {
      return { status: "absent", transitions: [] as const } as const;
    }
    if (
      raw.length >
      APPOINTMENT_WAITLIST_PENDING_TRANSITION_STORAGE_MAX_SERIALIZED_CHARACTERS
    ) {
      return { status: "unreadable", transitions: [] as const } as const;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      Array.isArray(parsed) ||
      Object.keys(parsed).length !== 2 ||
      (parsed as Record<string, unknown>).schemaVersion !== 1 ||
      !(parsed as Record<string, unknown>).entries ||
      typeof (parsed as Record<string, unknown>).entries !== "object" ||
      Array.isArray((parsed as Record<string, unknown>).entries)
    ) {
      return { status: "unreadable", transitions: [] as const } as const;
    }
    const rawEntries = Object.entries(
      (parsed as { entries: Record<string, unknown> }).entries,
    );
    if (rawEntries.length > MAX_PENDING_TRANSITIONS) {
      return { status: "unreadable", transitions: [] as const } as const;
    }
    const transitions: AppointmentWaitlistPendingTransitionV1[] = [];
    for (const [draftId, candidate] of rawEntries) {
      const validated = validatePending(candidate, now);
      if (
        !validated ||
        validated.waitlistDraftId !== draftId ||
        JSON.stringify(validated) !== JSON.stringify(candidate)
      ) {
        return { status: "unreadable", transitions: [] as const } as const;
      }
      transitions.push(validated);
    }
    return transitions.length === 0
      ? ({ status: "absent", transitions: [] as const } as const)
      : ({
          status: "present",
          transitions: Object.freeze(transitions),
        } as const);
  } catch {
    return { status: "unreadable", transitions: [] as const } as const;
  }
}

export function createAppointmentWaitlistPendingTransitionStrictPersisted(
  value: unknown,
  evaluatedAtEpochMs?: number,
) {
  const now = safePendingTransitionEpoch(evaluatedAtEpochMs);
  if (now === null) return { status: "invalid" } as const;
  if (isCustomerOwnedSessionStorageQuarantined()) {
    return { status: "unreadable" } as const;
  }
  try {
    const transition = validatePending(value, now);
    if (!transition || JSON.stringify(transition) !== JSON.stringify(value)) {
      return { status: "invalid" } as const;
    }
    const inspected =
      inspectAppointmentWaitlistPendingTransitionsPersisted(now);
    if (inspected.status === "unreadable") return inspected;
    if (
      inspected.transitions.some(
        ({ waitlistDraftId }) =>
          waitlistDraftId === transition.waitlistDraftId,
      )
    ) {
      return { status: "conflict", reason: "target-present" } as const;
    }
    if (inspected.transitions.length >= MAX_PENDING_TRANSITIONS) {
      return { status: "capacity" } as const;
    }
    const next = Object.fromEntries(
      [transition, ...inspected.transitions].map((candidate) => [
        candidate.waitlistDraftId,
        candidate,
      ]),
    );
    if (!writeEntries(next, now)) {
      return { status: "persistence-failed" } as const;
    }
    const confirmed =
      inspectAppointmentWaitlistPendingTransitionsPersisted(now);
    const exact = confirmed.transitions.find(
      ({ waitlistDraftId }) =>
        waitlistDraftId === transition.waitlistDraftId,
    );
    return confirmed.status === "present" &&
      exact &&
      JSON.stringify(exact) === JSON.stringify(transition)
      ? ({ status: "created", transition: exact } as const)
      : ({ status: "readback-mismatch" } as const);
  } catch {
    return { status: "unreadable" } as const;
  }
}

export function removeAppointmentWaitlistPendingTransitionStrictPersisted(
  value: unknown,
  evaluatedAtEpochMs?: number,
) {
  const now = safePendingTransitionEpoch(evaluatedAtEpochMs);
  if (now === null) return { status: "invalid" } as const;
  if (isCustomerOwnedSessionStorageQuarantined()) {
    return { status: "unreadable" } as const;
  }
  try {
    const expected = validatePending(value, now);
    if (!expected || JSON.stringify(expected) !== JSON.stringify(value)) {
      return { status: "invalid" } as const;
    }
    const inspected =
      inspectAppointmentWaitlistPendingTransitionsPersisted(now);
    if (inspected.status === "unreadable") return inspected;
    const current = inspected.transitions.find(
      ({ waitlistDraftId }) =>
        waitlistDraftId === expected.waitlistDraftId,
    );
    if (!current) return { status: "absent" } as const;
    if (JSON.stringify(current) !== JSON.stringify(expected)) {
      return { status: "conflict", reason: "target-mismatch" } as const;
    }
    const next = Object.fromEntries(
      inspected.transitions
        .filter(
          ({ waitlistDraftId }) =>
            waitlistDraftId !== expected.waitlistDraftId,
        )
        .map((candidate) => [candidate.waitlistDraftId, candidate]),
    );
    if (!writeEntries(next, now)) {
      return { status: "persistence-failed" } as const;
    }
    const confirmed =
      inspectAppointmentWaitlistPendingTransitionsPersisted(now);
    return confirmed.status !== "unreadable" &&
      !confirmed.transitions.some(
        ({ waitlistDraftId }) =>
          waitlistDraftId === expected.waitlistDraftId,
      )
      ? ({ status: "removed", transition: current } as const)
      : ({ status: "readback-mismatch" } as const);
  } catch {
    return { status: "unreadable" } as const;
  }
}

export function clearAppointmentWaitlistPendingTransition(
  waitlistDraftId: string,
  evaluatedAtEpochMs?: number,
) {
  if (!isIdentifier(waitlistDraftId) || typeof window === "undefined")
    return false;
  const now = safePendingTransitionEpoch(evaluatedAtEpochMs);
  if (now === null) return false;
  const entries = readEntries(now);
  delete entries[waitlistDraftId];
  return writeEntries(entries, now) && !readEntries(now)[waitlistDraftId];
}

export function clearAllAppointmentWaitlistPendingTransitions() {
  if (typeof window === "undefined") return false;
  try {
    window.sessionStorage.removeItem(
      APPOINTMENT_WAITLIST_PENDING_TRANSITION_STORAGE_KEY,
    );
    return (
      window.sessionStorage.getItem(
        APPOINTMENT_WAITLIST_PENDING_TRANSITION_STORAGE_KEY,
      ) === null
    );
  } catch {
    return false;
  }
}
